import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Polygon,
  Polyline,
  Marker,
  InfoWindow,
  OverlayView,
  Circle,
} from "@react-google-maps/api";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { Map as MapIcon, Satellite, Mountain, Layers, Check } from "lucide-react";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import {
  gisCoordinatesData,
  gisBlockCoordinatesData,
  gisStateCoordinates,
  gisVillageCoordinatesData,
  getfeaturefacility,
  getfacilitydatasearch,
  ladakhDistrictsInGis,
} from "../../../../Services/EndPoints";
import { toast } from "react-toastify";
import { getData } from "../../../../Services/Services";
import Loader from "react-spinner-loader";
import MapLegend from "./MapLegend";


// ──── NEW: Weather overlay import ────
import WeatherMapOverlay from "./WeatherMapOverlay";
import {
  geoJsonToGooglePaths,
  getBoundsFromPaths,
  getBoundsFromGeoJson,
  getCombinedBounds,
  getPathCentroid,
  calculateDistance,
  formatIncidentDateTime,
  facilityIconConfigs,
  normalizeFacilityValue,
  supportPointIconConfigs,
  disasterIconConfigs,
  buildFacilityPopupHtml,
  buildSupportPointPopupHtml,
} from "./GoogleMapsUtils";

// ─── Google Maps Config ─────────────────────────────────────────────────────
const GOOGLE_API_KEY = "AIzaSyDLzC-FhQmB8JS2H46OW46DNOEboBbrZVg";
const LIBRARIES = ["drawing", "geometry", "places", "visualization"];

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const DEFAULT_CENTER = { lat: 34.5, lng: 76.5 }; // Ladakh

// Map type mapping
const MAP_TYPE_MAP = {
  road: "roadmap",
  satellite: "satellite",
  terrain: "terrain",
  hybrid: "hybrid",
};

// ─── Marker interaction flag (module-level so overlays + polygons share it) ──
// ─── Marker interaction flag (module-level so overlays + polygons share it) ──
let _markerInteracting = false;
let _markerInteractTimer = null;

const setMarkerFlag = () => {
  _markerInteracting = true;
  clearTimeout(_markerInteractTimer);
  _markerInteractTimer = setTimeout(() => { _markerInteracting = false; }, 600);
};

const delayedPolygonAction = (action) => {
  setTimeout(() => {
    if (!_markerInteracting) {
      action();
    }
  }, 80);
};

// ─── Custom Overlay Marker Component ────────────────────────────────────────

// Persistent name label placed at a polygon's centroid (Affected Block /
// Affected District), as opposed to the old hover-only tooltip — keeps the
// name visible without the user having to mouse over the shaded area.
const PolygonNameLabel = ({ position, text, color }) => {
  if (!position || !text) return null;
  return (
    <CustomMarkerOverlay position={position} zIndex={1200}>
      <div
        style={{
          background: "#ffffff",
          color: "#0f172a",
          fontSize: "14px",
          fontWeight: 800,
          padding: "4px 12px",
          borderRadius: "7px",
          border: `2px solid ${color}`,
          whiteSpace: "nowrap",
          letterSpacing: "0.02em",
          textShadow: "0 1px 0 rgba(255,255,255,0.6)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        }}
      >
        {text}
      </div>
    </CustomMarkerOverlay>
  );
};

const CustomMarkerOverlay = ({
  position,
  children,
  onClick,
  zIndex = 0,
  offsetX = 0,
  offsetY = 0,
}) => {
  if (!position || !position.lat || !position.lng) return null;
  return (
    <OverlayView
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={() => ({ x: offsetX, y: offsetY })}
    >
      <div
        onMouseDown={(e) => {
          e.stopPropagation();
          setMarkerFlag();
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (e.nativeEvent) e.nativeEvent.stopImmediatePropagation();
          setMarkerFlag();
          if (onClick) onClick(e);
        }}
        style={{ cursor: onClick ? "pointer" : "default", zIndex, position: "relative" }}
      >
        {children}
      </div>
    </OverlayView>
  );
};

// ─── Icon Renderers ─────────────────────────────────────────────────────────

const FacilityMarkerContent = ({ facilityValue }) => {
  const config = facilityIconConfigs[facilityValue] || facilityIconConfigs["DEFAULT"];
  return (
    <div
      style={{
        background: "white",
        width: 38,
        height: 38,
        borderRadius: "50%",
        border: `3px solid ${config.color}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
      }}
    >
      <div
        style={{
          background: config.bgColor,
          width: 28,
          height: 28,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 15,
        }}
      >
        {config.emoji}
      </div>
    </div>
  );
};

// Small overlay on a facility marker showing route-to-incident status —
// spinner while computing, then a checkmark (active) or refresh (inactive,
// click again to re-show) once a route exists. Same treatment as the
// Resource Activity Panel's facility markers.
const RouteStatusBadge = ({ isSnapping, hasRoute, isActiveRoute }) => {
  if (isSnapping) {
    return (
      <div style={{ position: "absolute", top: "-8px", right: "-8px", width: "18px", height: "18px", borderRadius: "50%", border: "2px solid #e0e7ff", borderTopColor: "#3b82f6", animation: "spin 0.8s linear infinite", backgroundColor: "white" }} />
    );
  }
  if (hasRoute) {
    return (
      <div style={{ position: "absolute", top: "-8px", right: "-8px", width: "18px", height: "18px", borderRadius: "50%", backgroundColor: isActiveRoute ? "#059669" : "#9ca3af", border: "2px solid white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "9px", color: "white", fontWeight: 700 }}>
        {isActiveRoute ? "✓" : "↺"}
      </div>
    );
  }
  return null;
};

const SupportPointMarkerContent = ({ layerType }) => {
  const config = supportPointIconConfigs[layerType] || supportPointIconConfigs["DEFAULT"];
  return (
    <div
      style={{
        background: "white",
        width: 42,
        height: 42,
        borderRadius: "50%",
        border: `3px solid ${config.color}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 3px 12px rgba(0,0,0,0.2)",
        position: "relative",
      }}
    >
      <div
        style={{
          background: config.bgColor,
          width: 32,
          height: 32,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 17,
        }}
      >
        {config.emoji}
      </div>
      <div
        style={{
          position: "absolute",
          width: 11,
          height: 11,
          borderRadius: "50%",
          background: config.color,
          border: "2px solid white",
          bottom: 0,
          right: 2,
          zIndex: 2,
          boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
        }}
      />
    </div>
  );
};

// Same 4-tier scale/colors used for severity chips on the View Incident page
// — keyed off the "Level N" number so it doesn't matter whether the backend
// labels it "Major" or "Severe" etc.
const SEVERITY_MARKER_COLORS = {
  4: "#EF4444", // Critical
  3: "#FB923C", // Major / Severe
  2: "#FBBF24", // Moderate
  1: "#38BDF8", // Minor
};
const getSeverityMarkerColor = (severity) => {
  const match = /level\s*(\d)/i.exec(severity || "");
  return SEVERITY_MARKER_COLORS[match?.[1]] || "#DC2626";
};

// Impact-radius ring around the incident point scales with severity instead
// of always being a fixed 3000m, so a Critical incident visibly covers more
// ground on the map than a Minor one. Falls back to the old fixed radius
// when severity isn't available.
const SEVERITY_IMPACT_RADIUS_M = {
  4: 5000, // Critical
  3: 3500, // Major / Severe
  2: 2500, // Moderate
  1: 1500, // Minor
};
const getSeverityImpactRadius = (severity) => {
  const match = /level\s*(\d)/i.exec(severity || "");
  return SEVERITY_IMPACT_RADIUS_M[match?.[1]] || 3000;
};

const DisasterMarkerContent = ({ type, severity }) => {
  const disaster = disasterIconConfigs[type] || disasterIconConfigs.DEFAULT;
  const color = getSeverityMarkerColor(severity);
  return (
    <div style={{ position: "relative", width: 36, height: 36 }}>
      <div className="ripple-ring" style={{ borderColor: `${color}CC` }} />
      <div className="ripple-ring" style={{ animationDelay: "0.5s", borderColor: `${color}CC` }} />
      <div className="ripple-ring" style={{ animationDelay: "1s", borderColor: `${color}CC` }} />
      <div className="pulse-dot" style={{ background: `${color}99` }} />
      <div
        style={{
          background: "white",
          width: 36,
          height: 36,
          borderRadius: "50%",
          border: `3px solid ${color}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            background: color,
            width: 25,
            height: 25,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
          }}
        >
          {disaster.icon}
        </div>
      </div>
    </div>
  );
};

const BmskMarkerContent = () => (
  <div style={{ width: 32, height: 32 }}>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128" width="32" height="32">
      <circle cx="60" cy="60" r="60" fill="#f9385b" />
      <path
        fill="#ffffff"
        d="M64 30a24 24 0 0 0-24 24c0 18 24 40 24 40s24-22 24-40a24 24 0 0 0-24-24zm0 36a12 12 0 1 1 0-24 12 12 0 0 1 0 24z"
      />
    </svg>
  </div>
);

const EventMarkerContent = () => (
  <div style={{ width: 24, height: 24, color: "#cc0000" }}>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="24" height="24">
      <path
        fill="currentColor"
        d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480H40c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24V296c0 13.3 10.7 24 24 24s24-10.7 24-24V184c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z"
      />
    </svg>
  </div>
);

// ─── Main RMMap Component ───────────────────────────────────────────────────

const RMMap = ({
  containerHeight = "100vh",
  selectedDistrictIds,
  selectedDistrictId,
  selectedBlockIds,
  stateId,
  isLoading,
  incidentBlockIds,
  selectedRegions,
  selectedPanchayatIds,
  selectedVillageIds,
  selectedFacilities,
  mapData1,
  onLocationSelect,
  facilityCoordinates,
  bmskdetails,
  drawCircle,
  circleRadius,
  clearCircles,
  drawPolygon,
  incidentMarkerData,
  selectedIncidentDetails,
  supportPointsData,
  clearPolygons,
  selectedBmskids,
  handleDrawCreated,
  handleDrawDeleted,
  clickedPoint,
  setClickedPoint,
  bmskActiveDistricts,
  isKilometerEnabled,
  searchCoordinates,
  weatherData,
  weatherBoundaryGeoJson,
  createFacilityIcon: _createFacilityIcon,
  buildFacilityPopup: _buildFacilityPopup,
  showHeatmap,
  heatmapGridData,
  activeHeatmapLayer,
  activeHeatmapCondition,
  heatmapLoading,
  alertDistrictGeoJSON,   // array of GeoJSON features for districts
  alertBlockGeoJSON,      // array of GeoJSON features for blocks
  alertBoundariesLoading,
  isAlertsTab,       
  activeTab,
  regionManagement,
}) => {
  const handle = useFullScreenHandle();
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_API_KEY,
    libraries: LIBRARIES,
  });

  // ─── State ──────────────────────────────────────────────────────────────
  const [mapInstance, setMapInstance] = useState(null);
  const [mapZoom, setMapZoom] = useState(7);
  const [activeBaseLayer, setActiveBaseLayer] = useState("terrain");
  const [isLayerMenuOpen, setIsLayerMenuOpen] = useState(false);
  const [legendFilters, setLegendFilters] = useState({});
  const [activeInfoWindow, setActiveInfoWindow] = useState(null);
  // Switching tabs (Region/Search/Incident/Alerts) already clears the
  // markers themselves — an open popup should close along with them instead
  // of lingering on screen with nothing left pointing to it.
  useEffect(() => {
    setActiveInfoWindow(null);
  }, [activeTab]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loader, setLoader] = useState(false);

  // ─── Facility → Incident routing (same OSRM road-routing used on the
  // Resource Activity Panel's map, so clicking a facility while an incident
  // is selected draws the same purple route/distance badge here too). ───────
  const [facilityRoutes, setFacilityRoutes] = useState({});
  const [snappingFacilityId, setSnappingFacilityId] = useState(null);
  const [activeFacilityRouteId, setActiveFacilityRouteId] = useState(null);

  const incidentOrigin = useMemo(() => {
    const info = incidentMarkerData?.basicinfo?.[0];
    if (!info?.gisconfig) return null;
    try {
      const gisconfig = JSON.parse(info.gisconfig);
      const center = gisconfig?.incidentcenter;
      if (Array.isArray(center) && center.length >= 2) {
        const lat = center[1];
        const lng = center[0];
        if (!isNaN(lat) && !isNaN(lng)) return { lat: parseFloat(lat), lng: parseFloat(lng) };
      }
    } catch (e) {
      // ignore malformed gis_config
    }
    return null;
  }, [incidentMarkerData]);

  // Switching/clearing the selected incident invalidates any route already
  // drawn to it, so drop stale ones rather than leaving them on screen.
  useEffect(() => {
    setFacilityRoutes({});
    setActiveFacilityRouteId(null);
  }, [incidentOrigin]);

  // Switching tabs already clears the facility markers themselves — any
  // route drawn from one of them should disappear along with it instead of
  // being left floating with nothing left pointing to it.
  useEffect(() => {
    setFacilityRoutes({});
    setActiveFacilityRouteId(null);
  }, [activeTab]);

  const snapIncidentToFacility = useCallback(async (incidentCoords, facilityCoords, facilityKey) => {
    if (!incidentCoords || !facilityCoords) return;
    if (facilityRoutes[facilityKey]) {
      setActiveFacilityRouteId((prev) => (prev === facilityKey ? null : facilityKey));
      return;
    }
    setSnappingFacilityId(facilityKey);
    try {
      const routeUrl = `https://router.project-osrm.org/route/v1/driving/${incidentCoords.lng},${incidentCoords.lat};${facilityCoords.lng},${facilityCoords.lat}?overview=full&geometries=geojson`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000);
      const response = await fetch(routeUrl, { signal: controller.signal });
      clearTimeout(timeoutId);
      const data = await response.json();
      if (data.code === "Ok" && data.routes?.[0]) {
        const coords = data.routes[0].geometry.coordinates;
        const path = coords.map((c) => ({ lat: c[1], lng: c[0] }));
        const distanceKm = (data.routes[0].distance / 1000).toFixed(2);
        const durationMin = Math.round(data.routes[0].duration / 60);
        setFacilityRoutes((prev) => ({ ...prev, [facilityKey]: { path, distanceKm, durationMin } }));
        setActiveFacilityRouteId(facilityKey);
      } else {
        const path = [{ lat: incidentCoords.lat, lng: incidentCoords.lng }, { lat: facilityCoords.lat, lng: facilityCoords.lng }];
        setFacilityRoutes((prev) => ({ ...prev, [facilityKey]: { path, distanceKm: null, durationMin: null, isFallback: true } }));
        setActiveFacilityRouteId(facilityKey);
        toast.warning("Road routing unavailable — showing straight-line path");
      }
    } catch (error) {
      console.error("Facility route snapping error:", error);
      toast.error("Failed to compute route to facility");
    } finally {
      setSnappingFacilityId(null);
    }
  }, [facilityRoutes]);

  const [DistrictList, setDistrictList] = useState([]);
  const [districtLayers, setDistrictLayers] = useState({});
  const [blockLayers, setBlockLayers] = useState({});
  const [panchayatLayers, setPanchayatLayers] = useState({});
  const [villageLayers, setVillageLayers] = useState({});
  const [bmskLayers, setBmskLayers] = useState({});
  const [panchayatNamenew, setPanchayatNamenew] = useState({});
  // gisId -> name for "Affected District" polygons (blockLayers below) —
  // getBlockMap responses carry it as `blk_name`, getDistrictMap responses
  // (used for the incident's own district) carry it as `district`.
  const [blockNamenew, setBlockNamenew] = useState({});
  const [panchayatIncidentData, setPanchayatIncidentData] = useState([]);
  const [stateGeoJSON, setStateGeoJSON] = useState(null);

  const [selectedData, setSelectedData] = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [Filterfacility, setFilterfacility] = useState([]);
  const [filteredFacilities, setFilteredFacilities] = useState([]);

  // Drawing state
  const [drawnShapes, setDrawnShapes] = useState([]);
  const [drawingCircle, setDrawingCircle] = useState(null);
  const [drawingPolygon, setDrawingPolygon] = useState(null);
  const [drawingMode, setDrawingMode] = useState(null);
  // Manual polygon drawing — DrawingManager (google.maps.drawing) was
  // removed by Google starting Maps JS API v3.65, so "Draw Polygon" is
  // implemented by hand: each map click while drawingMode === "polygon"
  // appends a vertex here, and a double-click closes the shape.
  const [polygonDraftPoints, setPolygonDraftPoints] = useState([]);

  const mapRef = useRef(null);
  const circleRef = useRef(null);


  // ─── Legend filter helper ───────────────────────────────────────────────
  const isItemVisible = useCallback(
    (itemKey) => {
      if (itemKey && itemKey.startsWith("incident-")) return true;
      if (legendFilters[itemKey] !== undefined) return legendFilters[itemKey] !== false;
      if (itemKey) {
        const facilityMatch = itemKey.match(/^facility-merged-(.+)$/);
        const supportMatch = itemKey.match(/^support-(.+)$/);
        if (facilityMatch || supportMatch) {
          const baseType = facilityMatch ? facilityMatch[1] : supportMatch[1];
          const mergedKey = `merged-${baseType}`;
          if (legendFilters[mergedKey] !== undefined) return legendFilters[mergedKey] !== false;
        }
      }
      return true;
    },
    [legendFilters]
  );

  // Support-point markers that sit close together on screen would otherwise
  // stack on top of each other (e.g. several relief camps/hospitals near one
  // incident). A fixed geographic radius can't tell "close together" apart
  // from "far apart" consistently across zoom levels — a few hundred metres
  // looks overlapped when zoomed out to district level but perfectly spaced
  // once zoomed in to street level. So the grouping radius is derived from
  // the map's current zoom (standard Web Mercator metres-per-pixel formula)
  // and grouped points are fanned out by a fixed *pixel* offset — a
  // lightweight "spiderfy" that doesn't need a clustering library, since the
  // offset is purely visual (click targets still resolve to the real
  // lat/lng).
  const supportPointOffsets = useMemo(() => {
    const points = (supportPointsData || []).filter(
      (p) => p.latitude && p.longitude && !isNaN(p.latitude) && !isNaN(p.longitude)
    );
    if (points.length < 2) return {};

    const refLat = parseFloat(points[0].latitude);
    const metersPerPixel = (156543.03392 * Math.cos((refLat * Math.PI) / 180)) / Math.pow(2, mapZoom || 7);
    // Any two markers rendering within ~40px of each other get spread apart.
    const thresholdKm = (40 * metersPerPixel) / 1000;

    const assigned = new Array(points.length).fill(-1);
    const groups = [];
    points.forEach((p, i) => {
      if (assigned[i] !== -1) return;
      const group = [i];
      assigned[i] = groups.length;
      points.forEach((q, j) => {
        if (i === j || assigned[j] !== -1) return;
        const d = calculateDistance(
          parseFloat(p.latitude),
          parseFloat(p.longitude),
          parseFloat(q.latitude),
          parseFloat(q.longitude)
        );
        if (d <= thresholdKm) {
          group.push(j);
          assigned[j] = groups.length;
        }
      });
      groups.push(group);
    });

    const offsets = {};
    groups.forEach((group) => {
      if (group.length <= 1) return;
      const spread = 22;
      group.forEach((idx, k) => {
        const angle = (2 * Math.PI * k) / group.length;
        offsets[points[idx].id] = { x: Math.cos(angle) * spread, y: Math.sin(angle) * spread };
      });
    });
    return offsets;
  }, [supportPointsData, mapZoom]);

  // Build district GeoJSON array for heatmap clipping
  const districtGeoJSONForHeatmap = useMemo(() => {
    const features = [];

    // Use district layers if available
    Object.entries(districtLayers).forEach(([gisId, geoJson]) => {
      if (geoJson) {
        features.push({
          type: "Feature",
          properties: { gis_id: gisId },
          geometry: geoJson,
        });
      }
    });

    // If no district layers selected, fall back to state boundary
    if (features.length === 0 && stateGeoJSON) {
      features.push(stateGeoJSON);
    }

    return features;
  }, [districtLayers, stateGeoJSON]);

  const handleFilterChange = useCallback((filters) => {
    setLegendFilters(filters);
  }, []);

  // ─── Map callbacks ──────────────────────────────────────────────────────
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setMapInstance(map);
    setMapZoom(map.getZoom());
  }, []);

  // Tracked so the marker de-overlap spread below (supportPointOffsets) can
  // re-cluster using a pixel-distance threshold that actually matches what's
  // on screen — a fixed geographic radius would either over- or under-group
  // depending on how zoomed in the user currently is.
  const onMapZoomChanged = useCallback(() => {
    if (mapRef.current) setMapZoom(mapRef.current.getZoom());
  }, []);

  const onMapClick = useCallback(
    (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      if (drawingMode === "polygon") {
        setPolygonDraftPoints((prev) => [...prev, { lat, lng }]);
        return;
      }

      setClickedPoint({ lat, lng, timestamp: Date.now() });
      onLocationSelect({ latitude: lat, longitude: lng });
      setActiveInfoWindow(null);
    },
    [onLocationSelect, setClickedPoint, drawingMode]
  );

  // Double-click while drawing closes the polygon (needs >= 3 vertices),
  // mirroring DrawingManager's default finish gesture.
  const onMapDblClick = useCallback(() => {
    if (drawingMode !== "polygon") return;
    if (polygonDraftPoints.length < 3) {
      toast.info("Click at least 3 points to draw a polygon before finishing", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      return;
    }
    const path = polygonDraftPoints.map((p) => [p.lng, p.lat]);
    path.push(path[0]);
    setDrawnShapes([
      { type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [path] } },
    ]);
    setDrawingMode(null);
    setPolygonDraftPoints([]);
  }, [drawingMode, polygonDraftPoints]);

  // ─── Fetch data on mount ───────────────────────────────────────────────
  useEffect(() => {
    fetchDistrictList();
    mapStateCoordinates();
    getFacilityData();
  }, [stateId]);

  useEffect(() => {
    getFacilityData();
  }, [drawPolygon, drawCircle]);

  

  const getFacilityData = async () => {
    const response = await getData(getfacilitydatasearch.replace("STATEID", stateId));
    if (response) setFilterfacility(response);
  };

  const fetchDistrictList = async () => {
    if (!stateId) return;
    try {
      const response = await getData(ladakhDistrictsInGis + stateId);
      if (response && Array.isArray(response)) setDistrictList(response);
    } catch (error) {
      console.error("Error fetching districts:", error);
    }
  };

  const mapStateCoordinates = async () => {
    if (!stateId) return;
    try {
      const res = await getData(gisStateCoordinates + stateId);
      if (res && res.length > 0) {
        const stateData = res[0];
        const geoJsonData = {
          type: "Feature",
          properties: { name: stateData.state_name, state_id: stateData.state_id },
          geometry: JSON.parse(stateData.coordinates),
        };
        setStateGeoJSON(geoJsonData);
      }
    } catch (error) {
      console.error("Error fetching state coordinates:", error);
    }
  };

  // ─── Fetch layers when selections change ────────────────────────────────
 useEffect(() => {
    let cancelled = false; // ← cleanup flag

    const fetchLayers = async () => {
      if (isLoading) {
        setLoader(true);
        return;
      }
      try {
        setLoader(true);

        const districtPromises = selectedDistrictIds.map(async (gisId) => {
          try {
            const response = await getData(gisCoordinatesData + gisId);
            return response?.length > 0
              ? { gisId, geoJson: JSON.parse(response[0].coordinates) }
              : null;
          } catch { return null; }
        });

        const bmskPromises = Array.isArray(selectedBmskids)
          ? selectedBmskids.map(async (gisId) => {
              try {
                const response = await getData(gisCoordinatesData + gisId);
                return response?.length > 0
                  ? { gisId, geoJson: JSON.parse(response[0].coordinates) }
                  : null;
              } catch { return null; }
            })
          : [];

        const blockPromises = selectedBlockIds.map(async (gisId) => {
          try {
            const response = await getData(gisBlockCoordinatesData + gisId);
            return response?.length > 0
              ? { gisId, geoJson: JSON.parse(response[0].coordinates), name: response[0].blk_name }
              : null;
          } catch { return null; }
        });

        const incidentBlockPromises = incidentBlockIds.map(async (gisId) => {
          try {
            const response = await getData(gisCoordinatesData + gisId);
            return response?.length > 0
              ? { gisId, geoJson: JSON.parse(response[0].coordinates), name: response[0].district, source: "incident" }
              : null;
          } catch { return null; }
        });

        const panchayatPromises = selectedPanchayatIds.map(async (gisId) => {
          try {
            const response = await getData(gisBlockCoordinatesData + gisId);
            if (response?.length > 0) {
              return {
                gisId,
                geoJson: JSON.parse(response[0].coordinates),
                name: response[0].blk_name || response[0].gpname,
                incidentData: {
                  latitude: response[0].latitude,
                  longitude: response[0].longitude,
                  blk_name: response[0].blk_name,
                  dist_name: response[0].dist_name,
                  st_name: response[0].st_name,
                  near_town: response[0].near_town,
                },
              };
            }
            return null;
          } catch { return null; }
        });

        const villagePromises = selectedVillageIds.map(async (gisId) => {
          try {
            const response = await getData(gisVillageCoordinatesData + gisId);
            return response?.length > 0
              ? { gisId, geoJson: JSON.parse(response[0].coordinates) }
              : null;
          } catch { return null; }
        });

        const [districtResults, bmskResults, blockResults, incidentBlockResults, panchayatResults, villageResults] =
          await Promise.all([
            Promise.all(districtPromises),
            Promise.all(bmskPromises),
            Promise.all(blockPromises),
            Promise.all(incidentBlockPromises),
            Promise.all(panchayatPromises),
            Promise.all(villagePromises),
          ]);

        // ← If tab switched while awaiting, do NOT update state
        if (cancelled) return;

        const newDistrictLayers = {};
        districtResults.forEach((r) => { if (r) newDistrictLayers[r.gisId] = r.geoJson; });
        setDistrictLayers(newDistrictLayers);

        const newBmskLayers = {};
        bmskResults.forEach((r) => { if (r) newBmskLayers[r.gisId] = r.geoJson; });
        setBmskLayers(newBmskLayers);

        const newBlockLayers = {};
        const newBlockNames = {};
        blockResults.forEach((r) => { if (r) { newBlockLayers[r.gisId] = r.geoJson; newBlockNames[r.gisId] = r.name; } });
        incidentBlockResults.forEach((r) => { if (r) { newBlockLayers[r.gisId] = r.geoJson; newBlockNames[r.gisId] = r.name; } });
        setBlockLayers(newBlockLayers);
        setBlockNamenew(newBlockNames);

        const newPanchayatLayers = {};
        const panchayatNames = {};
        const incidentLocations = [];
        panchayatResults.forEach((r) => {
          if (r) {
            newPanchayatLayers[r.gisId] = r.geoJson;
            panchayatNames[r.gisId] = r.name;
            if (r.incidentData?.latitude && r.incidentData?.longitude) {
              incidentLocations.push({ ...r.incidentData, gisId: r.gisId, panchayatName: r.name });
            }
          }
        });
        setPanchayatLayers(newPanchayatLayers);
        setPanchayatNamenew(panchayatNames);
        setPanchayatIncidentData(incidentLocations);

        const newVillageLayers = {};
        villageResults.forEach((r) => { if (r) newVillageLayers[r.gisId] = r.geoJson; });
        setVillageLayers(newVillageLayers);

      } catch (error) {
        console.error("Error fetching layers:", error);
      } finally {
        if (!cancelled) setLoader(false);
      }
    };

    if (
      selectedDistrictIds.length > 0 ||
      selectedBlockIds.length > 0 ||
      incidentBlockIds.length > 0 ||
      selectedPanchayatIds.length > 0 ||
      selectedVillageIds.length > 0
    ) {
      fetchLayers();
    } else {
      // All selections cleared — immediately wipe all layers
      setDistrictLayers({});
      setBlockLayers({});
      setBlockNamenew({});
      setPanchayatLayers({});
      setVillageLayers({});
      setBmskLayers({});
      setPanchayatIncidentData([]);
      setPanchayatNamenew({});
      setLoader(false);
    }

    // ← This runs when deps change, setting cancelled=true BEFORE
    //   the new effect runs, so any in-flight fetch won't write state
    return () => { cancelled = true; };

  }, [selectedDistrictIds, selectedBlockIds, incidentBlockIds, selectedPanchayatIds, selectedVillageIds, isLoading, selectedBmskids]);

  // ─── Fly to bounds helpers ──────────────────────────────────────────────
  const flyToBounds = useCallback(
    (bounds, maxZoom = 10) => {
      if (!mapRef.current || !bounds || !bounds.getNorthEast) return;
      mapRef.current.fitBounds(bounds, { top: 50, bottom: 50, left: 50, right: 50 });
      const listener = mapRef.current.addListener("idle", () => {
        if (mapRef.current.getZoom() > maxZoom) {
          mapRef.current.setZoom(maxZoom);
        }
        window.google.maps.event.removeListener(listener);
      });
    },
    []
  );

  useEffect(() => {
    if (!isLoaded || !mapRef.current || selectedDistrictIds.length === 0) return;
    const layers = selectedDistrictIds.map((id) => districtLayers[id]).filter(Boolean);
    if (layers.length > 0) {
      const bounds = getCombinedBounds(layers);
      if (bounds) flyToBounds(bounds, selectedDistrictIds.length === 1 ? 10 : 9);
    }
  }, [selectedDistrictIds, districtLayers, isLoaded, flyToBounds]);

  // ─── Fly to weather boundary when selected ──────────────────────────
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !weatherBoundaryGeoJson?.geoJson) return;
    const bounds = getBoundsFromGeoJson(weatherBoundaryGeoJson.geoJson);
    if (bounds) flyToBounds(bounds, weatherBoundaryGeoJson.type === "block" ? 11 : 10);
  }, [weatherBoundaryGeoJson, isLoaded, flyToBounds]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || selectedBlockIds.length === 0) return;
    const layers = selectedBlockIds.map((id) => blockLayers[id]).filter(Boolean);
    if (layers.length > 0) {
      const bounds = getCombinedBounds(layers);
      if (bounds) flyToBounds(bounds, 10);
    }
  }, [selectedBlockIds, blockLayers, isLoaded, flyToBounds]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || selectedPanchayatIds.length === 0) return;
    const layers = selectedPanchayatIds.map((id) => panchayatLayers[id]).filter(Boolean);
    if (layers.length > 0) {
      const bounds = getCombinedBounds(layers);
      if (bounds) flyToBounds(bounds, 8);
    }
  }, [selectedPanchayatIds, panchayatLayers, isLoaded, flyToBounds]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || selectedVillageIds.length === 0) return;
    const layers = selectedVillageIds.map((id) => villageLayers[id]).filter(Boolean);
    if (layers.length > 0) {
      const bounds = getCombinedBounds(layers);
      if (bounds) flyToBounds(bounds, 18);
    }
  }, [selectedVillageIds, villageLayers, isLoaded, flyToBounds]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !selectedBmskids?.length) return;
    const layers = selectedBmskids.map((id) => bmskLayers[id]).filter(Boolean);
    if (layers.length > 0) {
      const bounds = getCombinedBounds(layers);
      if (bounds) flyToBounds(bounds, 10);
    }
  }, [selectedBmskids, bmskLayers, isLoaded, flyToBounds]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !searchCoordinates) return;
    const bounds = getBoundsFromGeoJson(searchCoordinates);
    if (bounds) flyToBounds(bounds, 22);
  }, [searchCoordinates, isLoaded, flyToBounds]);

  // ─── Zoom to state bounds on ANY tab switch ──────────────────────
useEffect(() => {
  if (!isLoaded || !mapRef.current || !stateGeoJSON) return;
  const bounds = getBoundsFromGeoJson(stateGeoJSON);
  if (bounds) {
    mapRef.current.fitBounds(bounds, {
      top: 50,
      bottom: 50,
      left: 50,
      right: 50,
    });
    // Prevent zooming out too far — ensure at least zoom 7
    const listener = mapRef.current.addListener("idle", () => {
      if (mapRef.current.getZoom() < 7) {
        mapRef.current.setZoom(7);
      }
      window.google.maps.event.removeListener(listener);
    });
  }
}, [activeTab, isLoaded, stateGeoJSON]);

  // ─── Drawing: Circle ────────────────────────────────────────────────────
  // "Click on the map to set circle center" is shown as an inline info
  // banner below the Drawing Tools buttons in GisMapview.js instead of a toast.
  useEffect(() => {
    if (drawCircle && mapRef.current) {
      setDrawingMode("circle");
      setDrawingPolygon(null);
    }
  }, [drawCircle]);

  useEffect(() => {
    if (drawingMode === "circle" && clickedPoint && circleRadius && mapRef.current) {
      setDrawingCircle({
        center: { lat: clickedPoint.lat, lng: clickedPoint.lng },
        radius: circleRadius,
      });
      setDrawingMode(null);
      setClickedPoint(null);
    }
  }, [clickedPoint, drawingMode, circleRadius]);

  useEffect(() => {
    if (clearCircles) {
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      setDrawingCircle(null);
      setDrawingMode(null);
      setDrawnShapes([]);
      setFilteredFacilities([]);
      setPolygonDraftPoints([]);
    }
  }, [clearCircles]);

  // ─── Drawing: Polygon ──────────────────────────────────────────────────
  // "Click points on the map to draw a polygon..." is shown as an inline
  // info banner below the Drawing Tools buttons in GisMapview.js instead of a toast.
  useEffect(() => {
    if (drawPolygon && mapRef.current) {
      setDrawingMode("polygon");
      setPolygonDraftPoints([]);
      setDrawingCircle(null);
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
    }
  }, [drawPolygon]);

  useEffect(() => {
    if (clearPolygons) {
      if (drawingPolygon && drawingPolygon.setMap) {
        drawingPolygon.setMap(null);
      }
      setDrawingPolygon(null);
      setDrawingMode(null);
      setDrawnShapes([]);
      setFilteredFacilities([]);
      setPolygonDraftPoints([]);
    }
  }, [clearPolygons]);

  useEffect(() => {
    if (drawingCircle) {
      const shape = {
        type: "Feature",
        properties: {
          isCircle: true,
          center: [drawingCircle.center.lat, drawingCircle.center.lng],
          radius: drawingCircle.radius,
        },
        geometry: { type: "Polygon", coordinates: [] },
      };
      setDrawnShapes([shape]);
    }
  }, [drawingCircle]);

  useEffect(() => {
    if (drawnShapes.length > 0 && Filterfacility.length > 0) {
      const filtered = Filterfacility.filter((facility) => {
        if (!facility.latitude || !facility.longitude || isNaN(facility.latitude) || isNaN(facility.longitude))
          return false;
        return drawnShapes.some((shape) => {
          if (shape.properties?.isCircle) {
            const center = shape.properties.center;
            const radius = shape.properties.radius;
            if (!center || !radius) return false;
            return calculateDistance(center[0], center[1], facility.latitude, facility.longitude) <= radius / 1000;
          } else {
            try {
              return booleanPointInPolygon([facility.longitude, facility.latitude], shape);
            } catch {
              return false;
            }
          }
        });
      });
      setFilteredFacilities(filtered);
    } else {
      setFilteredFacilities([]);
    }
  }, [drawnShapes, Filterfacility]);

  // ─── Marker click handler ──────────────────────────────────────────────
  const handleMarkerClick = async (id) => {
    if (selectedData[id]) return;
    setLoadingId(id);
    try {
      const facilitymarkerdetails = getfeaturefacility.replace("{ID}", id);
      const selectdetails = await getData(facilitymarkerdetails);
      setSelectedData((prev) => ({ ...prev, [id]: selectdetails }));
    } catch {
      setSelectedData((prev) => ({ ...prev, [id]: { error: "Failed to load data" } }));
    } finally {
      setLoadingId(null);
    }
  };

  const openInfoWindow = (type, id, position) => {
    setActiveInfoWindow({ type, id, position });
  };

  const toggleFullscreen = () => {
    // isFullscreen itself is kept in sync via <FullScreen onChange={setIsFullscreen}>,
    // which fires on every real fullscreen change — including the browser's own
    // Escape-key exit, not just clicks on this button.
    if (isFullscreen) handle.exit();
    else handle.enter();
  };

  // The container div now grows to 100vh in fullscreen (see containerHeight
  // usage below), but Google Maps doesn't repaint its internal canvas just
  // because its CSS box changed size — it needs an explicit "resize" event,
  // or the extra space stays blank until the map is panned/zoomed.
  useEffect(() => {
    if (!mapRef.current || !window.google) return;
    const id = setTimeout(() => {
      window.google.maps.event.trigger(mapRef.current, "resize");
    }, 150);
    return () => clearTimeout(id);
  }, [isFullscreen]);

  // ─── Finished polygon: made editable, keeps drawnShapes in sync with
  // vertex drags/inserts — same set_at/insert_at pattern the old
  // DrawingManager's onPolygonComplete used, just wired to the declarative
  // <Polygon>'s onLoad instead. ──────────────────────────────────────────
  const onFinishedPolygonLoad = useCallback((polygon) => {
    setDrawingPolygon(polygon);

    const updateShape = () => {
      const updatedPath = polygon.getPath().getArray().map((p) => [p.lng(), p.lat()]);
      updatedPath.push(updatedPath[0]);
      setDrawnShapes([
        { type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [updatedPath] } },
      ]);
    };
    window.google.maps.event.addListener(polygon.getPath(), "set_at", updateShape);
    window.google.maps.event.addListener(polygon.getPath(), "insert_at", updateShape);
  }, []);

  // ─── Render ─────────────────────────────────────────────────────────────
  if (loadError) return <div>Error loading Google Maps</div>;
  if (!isLoaded) return <div>Loading Maps…</div>;

  // Boundary polygons (state/district/block/panchayat/village/etc.) are
  // clickable by default so users can select them — but a clickable overlay
  // swallows the click before it ever reaches the map's own onClick, which
  // silently broke polygon/circle drawing since almost the entire viewport
  // is covered by one of these layers. Disable their hit-testing while
  // actively drawing so clicks fall through to the map underneath.
  const drawingActive = drawingMode === "polygon" || drawingMode === "circle";

  return (
    <FullScreen handle={handle} onChange={setIsFullscreen}>
      <style>{`
        @keyframes pulse-red {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.3); opacity: 0.5; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes ripple-red {
          0% { transform: scale(1); opacity: 0.8; }
          100% { transform: scale(2); opacity: 0; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .pulse-dot {
          position: absolute; width: 100%; height: 100%;
          border-radius: 50%; background: rgba(220,38,38,0.6);
          animation: pulse-red 2s ease-in-out infinite; z-index: 0;
        }
        .ripple-ring {
          position: absolute; width: 100%; height: 100%;
          border-radius: 50%; border: 3px solid rgba(220,38,38,0.8);
          animation: ripple-red 2s ease-out infinite; z-index: 0;
        }
        .gm-style-iw-c { max-width: 420px !important; }
      `}</style>

      <div style={{ display: "flex", height: isFullscreen ? "100vh" : containerHeight, fontFamily: "Arial, sans-serif", position: "relative" }}>
        {loader && <Loader show={loader} type="body" stack="vertical" message="Loading Data" />}

        <div style={{ flex: 3, position: "relative", width: "100%", height: "100%" }}>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={DEFAULT_CENTER}
            zoom={7}
            options={{
              mapTypeId: MAP_TYPE_MAP[activeBaseLayer] || "terrain",
              minZoom: 4,
              disableDefaultUI: true,
              zoomControl: !regionManagement,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              gestureHandling: "greedy", 
            }}
            onLoad={onMapLoad}
            onZoomChanged={onMapZoomChanged}
            onClick={onMapClick}
            onDblClick={onMapDblClick}
          >
            {/* ─── Polygon drawing (manual — DrawingManager/google.maps.drawing
                 was removed by Google starting Maps JS API v3.65) ────── */}
            {drawingMode === "polygon" && polygonDraftPoints.length > 0 && (
              <Polygon
                paths={polygonDraftPoints}
                options={{
                  fillColor: "#3388ff",
                  fillOpacity: 0.2,
                  strokeColor: "#3388ff",
                  strokeWeight: 2,
                  clickable: false,
                  editable: false,
                }}
              />
            )}
            {drawingMode !== "polygon" &&
              drawnShapes.length > 0 &&
              geoJsonToGooglePaths(drawnShapes[0]).map((path, i) => (
                <Polygon
                  key={`drawn-${i}`}
                  paths={path}
                  options={{
                    fillColor: "#3388ff",
                    fillOpacity: 0.2,
                    strokeColor: "#3388ff",
                    strokeWeight: 2,
                    editable: true,
                  }}
                  onLoad={onFinishedPolygonLoad}
                />
              ))}

            {/* ─── Editable Circle ─────────────────────────────────── */}
            {drawingCircle && (
  <Circle
    center={drawingCircle.center}
    radius={drawingCircle.radius}
    options={{
      fillColor: "#3388ff",
      fillOpacity: 0.2,
      strokeColor: "#3388ff",
      strokeWeight: 2,
      editable: true,
      draggable: true,
    }}
    onLoad={(circle) => {
      circleRef.current = circle;

      let dragTimer = null;

      const centerListener = window.google.maps.event.addListener(
        circle,
        "center_changed",
        () => {
          // Only update during actual user drag, not React re-renders
          if (!circleRef.current || !circle.getDraggable()) return;
          clearTimeout(dragTimer);
          dragTimer = setTimeout(() => {
            if (!circleRef.current) return;
            const c = circleRef.current.getCenter();
            const newLat = c.lat();
            const newLng = c.lng();
            const newRadius = circleRef.current.getRadius();
            setDrawingCircle((prev) => {
              if (!prev) return prev;
              // Skip update if nothing meaningfully changed
              if (
                Math.abs(prev.center.lat - newLat) < 0.000001 &&
                Math.abs(prev.center.lng - newLng) < 0.000001
              ) return prev;
              return { ...prev, center: { lat: newLat, lng: newLng }, radius: newRadius };
            });
          }, 100);
        }
      );

      const radiusListener = window.google.maps.event.addListener(
        circle,
        "radius_changed",
        () => {
          if (!circleRef.current) return;
          const newRadius = circleRef.current.getRadius();
          const c = circleRef.current.getCenter();
          setDrawingCircle((prev) => {
            if (!prev) return prev;
            if (Math.abs(prev.radius - newRadius) < 1) return prev;
            return { ...prev, center: { lat: c.lat(), lng: c.lng() }, radius: newRadius };
          });
        }
      );

      circle._centerListener = centerListener;
      circle._radiusListener = radiusListener;
      circle._dragTimer = dragTimer;
    }}
    onUnmount={() => {
      if (circleRef.current) {
        clearTimeout(circleRef.current._dragTimer);
        if (circleRef.current._centerListener) {
          window.google.maps.event.removeListener(circleRef.current._centerListener);
        }
        if (circleRef.current._radiusListener) {
          window.google.maps.event.removeListener(circleRef.current._radiusListener);
        }
      }
      circleRef.current = null;
    }}
  />
)}

            {/* ─── State Boundary ────────────────────────────────── */}
            {stateGeoJSON &&
              geoJsonToGooglePaths(stateGeoJSON).map((path, i) => (
                <Polygon
                  key={`state-${i}`}
                  paths={path}
                  options={{
                    fillColor: "transparent",
                    fillOpacity: 0.2,
                    strokeColor: "#000000",
                    strokeWeight: 2,
                    clickable: !drawingActive,
                  }}
                  onClick={(e) => {
                    const lat = e.latLng.lat(), lng = e.latLng.lng();
                    delayedPolygonAction(() => {
                      onLocationSelect({ latitude: lat, longitude: lng });
                    });
                  }}
                />
              ))}

            {/* ─── Search Coordinates ────────────────────────────── */}
            {searchCoordinates &&
              geoJsonToGooglePaths(searchCoordinates).map((path, i) => (
                <Polygon
                  key={`search-${i}`}
                  paths={path}
                  options={{
                    fillColor: "orange",
                    fillOpacity: 0.3,
                    strokeColor: "orange",
                    strokeWeight: 3,
                    clickable: !drawingActive,
                  }}
                  onClick={(e) => {
                    const lat = e.latLng.lat(), lng = e.latLng.lng();
                    delayedPolygonAction(() => {
                      onLocationSelect({ latitude: lat, longitude: lng });
                    });
                  }}
                />
              ))}

            {/* ─── District Layers ───────────────────────────────── */}
            {Object.entries(districtLayers).map(([gisId, geoJson]) => {
              const district = DistrictList.find((d) => d.gis_id === parseInt(gisId));
              return geoJsonToGooglePaths(geoJson).map((path, i) => (
                <Polygon
                  key={`district-${gisId}-${i}`}
                  paths={path}
                  options={{
                    fillColor: "#ff7800",
                    fillOpacity: 0.3,
                    strokeColor: "#ff7800",
                    strokeWeight: 1,
                    clickable: !drawingActive,
                  }}
                  onClick={(e) => {
                      const lat = e.latLng.lat(), lng = e.latLng.lng();
                      const savedPath = path;
                      delayedPolygonAction(() => {
                        onLocationSelect({ latitude: lat, longitude: lng });
                        const bounds = getBoundsFromPaths([savedPath]);
                        if (bounds) mapRef.current?.fitBounds(bounds);
                      });
                    }}
                  onMouseOver={(e) => {
                    openInfoWindow("district-tooltip", gisId, { lat: e.latLng.lat(), lng: e.latLng.lng() });
                  }}
                  onMouseOut={() => {
                    if (activeInfoWindow?.type === "district-tooltip") setActiveInfoWindow(null);
                  }}
                />
              ));
            })}

            {activeInfoWindow?.type === "district-tooltip" && (
              <InfoWindow position={activeInfoWindow.position} onCloseClick={() => setActiveInfoWindow(null)} options={{ disableAutoPan: true }}>
                <b>{DistrictList.find((d) => d.gis_id === parseInt(activeInfoWindow.id))?.value || `District ${activeInfoWindow.id}`}</b>
              </InfoWindow>
            )}

            {/* ─── BMSK Layers ───────────────────────────────────── */}
            {Object.entries(bmskLayers).map(([gisId, geoJson]) => {
              return geoJsonToGooglePaths(geoJson).map((path, i) => (
                <Polygon
                  key={`bmsk-layer-${gisId}-${i}`}
                  paths={path}
                  options={{ fillColor: "#ff7800", fillOpacity: 0.3, strokeColor: "#ff7800", strokeWeight: 1, clickable: !drawingActive }}
                  onClick={(e) => {
                    const lat = e.latLng.lat(), lng = e.latLng.lng();
                    const savedPath = path;
                    delayedPolygonAction(() => {
                      onLocationSelect({ latitude: lat, longitude: lng });
                      const bounds = getBoundsFromPaths([savedPath]);
                      if (bounds) mapRef.current?.fitBounds(bounds);
                    });
                  }}
                />
              ));
            })}

            {/* ─── Block Layers ("Affected District" in the legend) ──
                Always rendered in the legend's green family — the incident's
                own block is only distinguished by a bolder stroke/opacity,
                not a different hue, so it can't be mistaken for the blue
                "Affected Block" (panchayat) shape below. */}
            {isItemVisible("region-block") &&
              Object.entries(blockLayers).map(([gisId, geoJson]) => {
                const isIncidentBlock = incidentBlockIds.includes(parseInt(gisId));
                const incidentRegion = incidentMarkerData?.regions?.find((r) => r.block_gis_id === parseInt(gisId));
                return geoJsonToGooglePaths(geoJson).map((path, i) => (
                  <Polygon
                    key={`block-${gisId}-${i}`}
                    paths={path}
                    options={{
                      fillColor: "#22c55e",
                      fillOpacity: isIncidentBlock ? 0.28 : 0.16,
                      strokeColor: isIncidentBlock ? "#15803d" : "#22c55e",
                      strokeWeight: isIncidentBlock ? 2 : 1,
                      clickable: !drawingActive,
                    }}
                    onClick={(e) => {
                      const savedPath = path;
                      delayedPolygonAction(() => {
                        const bounds = getBoundsFromPaths([savedPath]);
                        if (bounds) mapRef.current?.fitBounds(bounds);
                      });
                    }}
                    onMouseOver={(e) => {
                      openInfoWindow("block-tooltip", gisId, { lat: e.latLng.lat(), lng: e.latLng.lng() });
                    }}
                    onMouseOut={() => {
                      if (activeInfoWindow?.type === "block-tooltip") setActiveInfoWindow(null);
                    }}
                  />
                ));
              })}

            {activeInfoWindow?.type === "block-tooltip" && (
              <InfoWindow position={activeInfoWindow.position} onCloseClick={() => setActiveInfoWindow(null)} options={{ disableAutoPan: true }}>
                <b>
                  {blockNamenew[activeInfoWindow.id] ||
                    selectedRegions.find((r) => r.gis_id === parseInt(activeInfoWindow.id))?.name ||
                    incidentMarkerData?.regions?.find((r) => r.block_gis_id === parseInt(activeInfoWindow.id))?.blockname ||
                    `Block ${activeInfoWindow.id}`}
                </b>
              </InfoWindow>
            )}

            {/* Persistent "Affected District" name label at each block's centroid
                — was previously hover-only via the tooltip above. */}
            {isItemVisible("region-block") &&
              Object.entries(blockLayers).map(([gisId, geoJson]) => {
                const paths = geoJsonToGooglePaths(geoJson);
                const centroid = paths[0] ? getPathCentroid(paths[0]) : null;
                const name =
                  blockNamenew[gisId] ||
                  selectedRegions.find((r) => r.gis_id === parseInt(gisId))?.name ||
                  incidentMarkerData?.regions?.find((r) => r.block_gis_id === parseInt(gisId))?.blockname ||
                  null;
                if (!centroid || !name) return null;
                return (
                  <PolygonNameLabel key={`block-label-${gisId}`} position={centroid} text={name} color="#15803d" />
                );
              })}

            {/* ─── Panchayat Layers ("Affected Block" in the legend) ──
                Always rendered in the legend's blue family for the same
                reason as above. */}
            {isItemVisible("region-panchayat") &&
              Object.entries(panchayatLayers).map(([gisId, geoJson]) => {
                const incidentPanchayat = incidentMarkerData?.regions?.find((r) => r.gis_id === parseInt(gisId));
                const isIncidentPanchayat = !!incidentPanchayat;
                return geoJsonToGooglePaths(geoJson).map((path, i) => (
                  <Polygon
                    key={`panchayat-${gisId}-${i}`}
                    paths={path}
                    options={{
                      // The incident's exact point already gets a red impact-
                      // radius circle (see Incident Center Marker) — this
                      // administrative boundary is shown in blue instead of
                      // also shading the whole panchayat red.
                      fillColor: "#3B82F6",
                      fillOpacity: isIncidentPanchayat ? 0.28 : 0.16,
                      strokeColor: isIncidentPanchayat ? "#1d4ed8" : "#3B82F6",
                      strokeWeight: isIncidentPanchayat ? 2 : 1,
                      clickable: !drawingActive,
                    }}
                    onClick={(e) => {
                      const lat = e.latLng.lat(), lng = e.latLng.lng();
                      const savedGisId = gisId;
                      const savedPath = path;
                      const savedIsIncident = isIncidentPanchayat;
                      delayedPolygonAction(() => {
                        if (savedIsIncident && incidentMarkerData) {
                          openInfoWindow("panchayat-incident", savedGisId, { lat, lng });
                        } else {
                          const bounds = getBoundsFromPaths([savedPath]);
                          if (bounds) mapRef.current?.fitBounds(bounds);
                        }
                      });
                    }}
                  />
                ));
              })}

            {/* Persistent "Affected Block" name label at each panchayat's centroid. */}
            {isItemVisible("region-panchayat") &&
              Object.entries(panchayatLayers).map(([gisId, geoJson]) => {
                const paths = geoJsonToGooglePaths(geoJson);
                const centroid = paths[0] ? getPathCentroid(paths[0]) : null;
                const name = panchayatNamenew?.[gisId] || null;
                if (!centroid || !name) return null;
                return (
                  <PolygonNameLabel key={`panchayat-label-${gisId}`} position={centroid} text={name} color="#1d4ed8" />
                );
              })}

            {activeInfoWindow?.type === "panchayat-incident" && incidentMarkerData && (
              <InfoWindow position={activeInfoWindow.position} onCloseClick={() => setActiveInfoWindow(null)}>
                <div
                  style={{ fontFamily: "Arial", minWidth: 280, maxWidth: 400 }}
                  dangerouslySetInnerHTML={{
                    __html: buildIncidentPopupContent(
                      incidentMarkerData,
                      incidentMarkerData.regions?.find((r) => r.gis_id === parseInt(activeInfoWindow.id)),
                      selectedIncidentDetails
                    ),
                  }}
                />
              </InfoWindow>
            )}

            {/* ─── Village Layers ────────────────────────────────── */}
            {Object.entries(villageLayers).map(([gisId, geoJson]) => {
              return geoJsonToGooglePaths(geoJson).map((path, i) => (
                <Polygon
                  key={`village-${gisId}-${i}`}
                  paths={path}
                  options={{ fillColor: "#aa00aa", fillOpacity: 0.4, strokeColor: "#aa00aa", strokeWeight: 1, clickable: !drawingActive }}
                  onClick={() => {
                    const savedPath = path;
                    delayedPolygonAction(() => {
                      const bounds = getBoundsFromPaths([savedPath]);
                      if (bounds) mapRef.current?.fitBounds(bounds);
                    });
                  }}
                />
              ));
            })}

            {/* ─── Clicked Point Marker ──────────────────────────── */}
            {clickedPoint && isKilometerEnabled && (
              <Marker
                position={{ lat: clickedPoint.lat, lng: clickedPoint.lng }}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 6,
                  fillColor: "red",
                  fillOpacity: 1,
                  strokeWeight: 1,
                  strokeColor: "red",
                }}
              />
            )}

            {/* ─── Selected Facility Markers ─────────────────────── */}
            {selectedFacilities?.map((facility) => {
              const { latitude, longitude, id } = facility;
              if (typeof latitude !== "number" || typeof longitude !== "number" || isNaN(latitude) || isNaN(longitude)) return null;
              const fType = normalizeFacilityValue(facility);
              const itemKey = `facility-merged-${fType}`;
              if (!isItemVisible(itemKey)) return null;
              const facilityKey = `fac-sel-${id}`;
              const isSnapping = snappingFacilityId === facilityKey;
              const hasRoute = !!facilityRoutes[facilityKey];
              const isActiveRoute = activeFacilityRouteId === facilityKey;
              return (
                <CustomMarkerOverlay
                  key={facilityKey}
                  position={{ lat: latitude, lng: longitude }}
                  offsetX={-19}
                  offsetY={-19}
                  onClick={() => {
                    handleMarkerClick(id);
                    openInfoWindow("facility", id, { lat: latitude, lng: longitude });
                    if (incidentOrigin) snapIncidentToFacility(incidentOrigin, { lat: latitude, lng: longitude }, facilityKey);
                  }}
                >
                  <div style={{ position: "relative" }}>
                    <FacilityMarkerContent facilityValue={fType} />
                    <RouteStatusBadge isSnapping={isSnapping} hasRoute={hasRoute} isActiveRoute={isActiveRoute} />
                  </div>
                </CustomMarkerOverlay>
              );
            })}

            {facilityCoordinates?.map((facility) => {
              const fType = normalizeFacilityValue(facility);
              const itemKey = `facility-merged-${fType}`;
              if (!isItemVisible(itemKey)) return null;
              const [lat, lng] = facility.position || [0, 0];
              const facilityKey = `fac-coord-${facility.id}`;
              const isSnapping = snappingFacilityId === facilityKey;
              const hasRoute = !!facilityRoutes[facilityKey];
              const isActiveRoute = activeFacilityRouteId === facilityKey;
              return (
                <CustomMarkerOverlay
                  key={facilityKey}
                  position={{ lat, lng }}
                  offsetX={-19}
                  offsetY={-19}
                  onClick={() => {
                    handleMarkerClick(facility.id);
                    openInfoWindow("facility-coord", facility.id, { lat, lng });
                    if (incidentOrigin) snapIncidentToFacility(incidentOrigin, { lat, lng }, facilityKey);
                  }}
                >
                  <div style={{ position: "relative" }}>
                    <FacilityMarkerContent facilityValue={fType} />
                    <RouteStatusBadge isSnapping={isSnapping} hasRoute={hasRoute} isActiveRoute={isActiveRoute} />
                  </div>
                </CustomMarkerOverlay>
              );
            })}

            {filteredFacilities?.map((facility) => {
              if (isNaN(facility.latitude) || isNaN(facility.longitude)) return null;
              const fType = normalizeFacilityValue(facility);
              const itemKey = `facility-merged-${fType}`;
              if (!isItemVisible(itemKey)) return null;
              const facilityKey = `fac-filt-${facility.id}`;
              const isSnapping = snappingFacilityId === facilityKey;
              const hasRoute = !!facilityRoutes[facilityKey];
              const isActiveRoute = activeFacilityRouteId === facilityKey;
              return (
                <CustomMarkerOverlay
                  key={facilityKey}
                  position={{ lat: facility.latitude, lng: facility.longitude }}
                  offsetX={-19}
                  offsetY={-19}
                  onClick={() => {
                    handleMarkerClick(facility.id);
                    openInfoWindow("facility-filtered", facility.id, { lat: facility.latitude, lng: facility.longitude });
                    if (incidentOrigin) snapIncidentToFacility(incidentOrigin, { lat: facility.latitude, lng: facility.longitude }, facilityKey);
                  }}
                >
                  <div style={{ position: "relative" }}>
                    <FacilityMarkerContent facilityValue={fType} />
                    <RouteStatusBadge isSnapping={isSnapping} hasRoute={hasRoute} isActiveRoute={isActiveRoute} />
                  </div>
                </CustomMarkerOverlay>
              );
            })}

            {/* ─── Facility → Incident Route ──────────────────────── */}
            {activeFacilityRouteId && facilityRoutes[activeFacilityRouteId] && (() => {
              const route = facilityRoutes[activeFacilityRouteId];
              const mid = route.path[Math.floor(route.path.length / 2)];
              return (
                <>
                  <Polyline path={route.path} options={{ strokeColor: "#7c3aed", strokeWeight: 8, strokeOpacity: 0.18, zIndex: 5 }} />
                  <Polyline
                    path={route.path}
                    options={{
                      strokeColor: route.isFallback ? "#f59e0b" : "#7c3aed",
                      strokeWeight: 4,
                      strokeOpacity: 0.85,
                      strokeDashArray: route.isFallback ? "8 6" : undefined,
                      zIndex: 6,
                      icons: route.isFallback
                        ? undefined
                        : [{ icon: { path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW, scale: 3, strokeColor: "#7c3aed", fillColor: "#7c3aed", fillOpacity: 1 }, offset: "50%", repeat: "120px" }],
                    }}
                    onClick={() => setActiveFacilityRouteId(null)}
                  />
                  {route.distanceKm && mid && (
                    <OverlayView position={mid} mapPaneName={OverlayView.FLOAT_PANE} getPixelPositionOffset={(w, h) => ({ x: -w / 2, y: -h - 8 })}>
                      <div
                        onClick={() => setActiveFacilityRouteId(null)}
                        style={{
                          backgroundColor: "#7c3aed", color: "white", padding: "5px 12px", borderRadius: "20px",
                          fontSize: "12px", fontWeight: 700, boxShadow: "0 3px 10px rgba(124,58,237,0.45)",
                          whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", border: "2px solid white",
                        }}
                      >
                        🛣️ {route.distanceKm} km &nbsp;·&nbsp; ⏱ {route.durationMin} min &nbsp;<span style={{ fontSize: "10px", opacity: 0.8 }}>✕</span>
                      </div>
                    </OverlayView>
                  )}
                </>
              );
            })()}

            {/* ─── Facility InfoWindow ───────────────────────────── */}
            {activeInfoWindow &&
              (activeInfoWindow.type === "facility" || activeInfoWindow.type === "facility-coord" || activeInfoWindow.type === "facility-filtered") && (
                <InfoWindow position={activeInfoWindow.position} onCloseClick={() => setActiveInfoWindow(null)}>
                  <div>
                    {loadingId === activeInfoWindow.id && <p>Loading details...</p>}
                    {selectedData[activeInfoWindow.id]?.error && (
                      <p style={{ color: "red" }}>{selectedData[activeInfoWindow.id].error}</p>
                    )}
                    {selectedData[activeInfoWindow.id] && !selectedData[activeInfoWindow.id].error ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: buildFacilityPopupHtml(
                            findFacilityById(activeInfoWindow.id, selectedFacilities, facilityCoordinates, filteredFacilities),
                            selectedData[activeInfoWindow.id]
                          ),
                        }}
                      />
                    ) : (
                      !loadingId && (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: buildFacilityPopupHtml(
                              findFacilityById(activeInfoWindow.id, selectedFacilities, facilityCoordinates, filteredFacilities),
                              null
                            ),
                          }}
                        />
                      )
                    )}
                  </div>
                </InfoWindow>
              )}

            {/* ─── Event Markers ─────────────────────────────────── */}
            {mapData1?.events
              ?.filter((event) => {
                const lat = parseFloat(event?.position?.[0]);
                const lng = parseFloat(event?.position?.[1]);
                return !isNaN(lat) && !isNaN(lng) && isItemVisible("event-marker");
              })
              .map((event) => (
                <CustomMarkerOverlay
                  key={`event-${event.id}`}
                  position={{ lat: parseFloat(event.position[0]), lng: parseFloat(event.position[1]) }}
                  offsetX={-12}
                  offsetY={-12}
                  onClick={() => {
                    openInfoWindow("event", event.id, {
                      lat: parseFloat(event.position[0]),
                      lng: parseFloat(event.position[1]),
                    });
                  }}
                >
                  <EventMarkerContent />
                </CustomMarkerOverlay>
              ))}

            {activeInfoWindow?.type === "event" && (() => {
              const event = mapData1?.events?.find((e) => e.id === activeInfoWindow.id);
              if (!event) return null;
              return (
                <InfoWindow position={activeInfoWindow.position} onCloseClick={() => setActiveInfoWindow(null)}>
                  <div style={{ minWidth: 200, maxWidth: 400 }}>
                    <p><strong>Incident Name:</strong> {event.incidentName}</p>
                    <p><strong>Incident Category:</strong> {event.incidentCategory}</p>
                    <p><strong>Event Date and Time:</strong> {event.eventTime}</p>
                    <p><strong>User Details:</strong> {event.userName},{event.jobTitle},{event.mobileNumber}</p>
                    <p><strong>Lat/Lng:</strong> {event.position?.[0]}/{event.position?.[1]}</p>
                    <p><strong>Event Description:</strong> {event.action}</p>
                    {event.files?.length > 0 && (
                      <div style={{ marginTop: 5 }}>
                        {event.files.map((file, idx) => {
                          if (!file?.filePath || idx !== currentImageIndex) return null;
                          const isVideo = /\.(mp4|webm|ogg)$/i.test(file.filePath);
                          return (
                            <div key={file.fileId}>
                              {isVideo ? (
                                <video controls style={{ width: "100%", height: 150, objectFit: "cover", borderRadius: 4 }}>
                                  <source src={file.filePath} type="video/mp4" />
                                </video>
                              ) : (
                                <img src={file.filePath} alt="Preview" style={{ width: "100%", height: 150, objectFit: "cover", borderRadius: 4 }} />
                              )}
                              {event.files.length > 1 && (
                                <div style={{ textAlign: "center", marginTop: 4, fontSize: 12 }}>
                                  <button onClick={() => setCurrentImageIndex((p) => (p === 0 ? event.files.length - 1 : p - 1))}>&lt;</button>
                                  {" "}{currentImageIndex + 1} / {event.files.length}{" "}
                                  <button onClick={() => setCurrentImageIndex((p) => (p === event.files.length - 1 ? 0 : p + 1))}>&gt;</button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </InfoWindow>
              );
            })()}

            {/* ─── BMSK Weather Markers ──────────────────────────── */}
            {bmskdetails?.map((station) => {
              if (!isItemVisible("bmsk-weather")) return null;
              return (
                <CustomMarkerOverlay
                  key={`bmsk-m-${station.id}`}
                  position={{ lat: station.latitude, lng: station.longitude }}
                  offsetX={-16}
                  offsetY={-16}
                  onClick={() => {
                    openInfoWindow("bmsk", station.id, { lat: station.latitude, lng: station.longitude });
                  }}
                >
                  <BmskMarkerContent />
                </CustomMarkerOverlay>
              );
            })}

            {activeInfoWindow?.type === "bmsk" && (() => {
              const station = bmskdetails?.find((s) => s.id === activeInfoWindow.id);
              if (!station) return null;
              return (
                <InfoWindow position={activeInfoWindow.position} onCloseClick={() => setActiveInfoWindow(null)}>
                  <div style={{ minWidth: 200 }}>
                    <p><strong>District:</strong> {station.district}</p>
                    <p><strong>Temperature:</strong> {station.temperature}</p>
                    <p><strong>Humidity:</strong> {station.humidity}</p>
                    <p><strong>Rainfall:</strong> {station.rainfall}</p>
                    <p><strong>Wind Speed:</strong> {station.windSpeed}</p>
                  </div>
                </InfoWindow>
              );
            })()}

            {/* ─── Incident Disaster Markers ──────────────────────── */}
            {panchayatIncidentData?.length > 0 &&
              !incidentMarkerData?.basicinfo?.[0]?.gisconfig &&
              panchayatIncidentData.map((incident, index) => {
                if (!incident.latitude || !incident.longitude || isNaN(incident.latitude) || isNaN(incident.longitude)) return null;
                const matchingIncident = incidentMarkerData?.regions?.find((r) => r.gis_id === parseInt(incident.gisId));
                if (!matchingIncident || !incidentMarkerData) return null;
                const incidentCategory = incidentMarkerData.basicinfo?.[0]?.cat || "DEFAULT";
                return (
                  <CustomMarkerOverlay
                    key={`panchayat-incident-${incident.gisId}-${index}`}
                    position={{ lat: parseFloat(incident.latitude), lng: parseFloat(incident.longitude) }}
                    offsetX={-24}
                    offsetY={-24}
                    zIndex={1000}
                    onClick={() => {
                      openInfoWindow("panchayat-incident-marker", `${incident.gisId}-${index}`, {
                        lat: parseFloat(incident.latitude),
                        lng: parseFloat(incident.longitude),
                      });
                    }}
                  >
                    <DisasterMarkerContent type={incidentCategory} severity={selectedIncidentDetails?.severity} />
                  </CustomMarkerOverlay>
                );
              })}

            {activeInfoWindow?.type === "panchayat-incident-marker" && incidentMarkerData && (
              <InfoWindow position={activeInfoWindow.position} onCloseClick={() => setActiveInfoWindow(null)}>
                <div
                  style={{ fontFamily: "Arial", minWidth: 280, maxWidth: 400 }}
                  dangerouslySetInnerHTML={{
                    __html: buildIncidentPopupContent(
                      incidentMarkerData,
                      incidentMarkerData.regions?.find((r) => r.gis_id === parseInt(activeInfoWindow.id.split("-")[0])),
                      selectedIncidentDetails
                    ),
                  }}
                />
              </InfoWindow>
            )}

            {/* ─── Incident Center Marker ────────────────────────── */}
            {incidentMarkerData?.basicinfo?.map((info, index) => {
              try {
                const gisconfig = info.gisconfig ? JSON.parse(info.gisconfig) : null;
                if (!gisconfig?.incidentcenter?.length >= 2) return null;
                const lat = gisconfig.incidentcenter[1];
                const lng = gisconfig.incidentcenter[0];
                if (isNaN(lat) || isNaN(lng)) return null;
                const cat = info.cat || "DEFAULT";
                if (!isItemVisible(`incident-${cat}`)) return null;
                return (
                  <React.Fragment key={`incident-center-${info.id}-${index}`}>
                    {/* Red impact-radius ring around the exact incident point,
                        instead of shading the whole affected block/district red.
                        Radius scales with severity rather than a fixed 3000m. */}
                    <Circle
                      center={{ lat, lng }}
                      radius={getSeverityImpactRadius(info.severity || selectedIncidentDetails?.severity)}
                      options={{
                        fillColor: "#B91C1C",
                        fillOpacity: 0.16,
                        strokeColor: "#B91C1C",
                        strokeOpacity: 0.8,
                        strokeWeight: 1.5,
                        clickable: false,
                        zIndex: 500,
                      }}
                    />
                    <CustomMarkerOverlay
                      position={{ lat, lng }}
                      offsetX={-24}
                      offsetY={-24}
                      zIndex={2000}
                      onClick={() => {
                        openInfoWindow("incident-center", `${info.id}-${index}`, { lat, lng });
                      }}
                    >
                      <DisasterMarkerContent type={cat} severity={info.severity || selectedIncidentDetails?.severity} />
                    </CustomMarkerOverlay>
                  </React.Fragment>
                );
              } catch {
                return null;
              }
            })}

            {activeInfoWindow?.type === "incident-center" && incidentMarkerData && (
              <InfoWindow position={activeInfoWindow.position} onCloseClick={() => setActiveInfoWindow(null)}>
                <div
                  style={{ fontFamily: "Arial", minWidth: 280, maxWidth: 400 }}
                  dangerouslySetInnerHTML={{ __html: buildIncidentCenterPopup(incidentMarkerData, selectedIncidentDetails) }}
                />
              </InfoWindow>
            )}

            {/* ─── Flooded Zone area overlay ───────────────────────
                A Flooded Zone support point still gets its pin (below), but
                also a translucent, dashed-look circle so it reads as an
                impacted area rather than a single spot — no polygon extent
                is available from the API, so this approximates the zone. */}
            {supportPointsData?.map((point) => {
              if (point.layerType !== "FloodedZone") return null;
              if (!point.latitude || !point.longitude || isNaN(point.latitude) || isNaN(point.longitude)) return null;
              if (!isItemVisible(`support-${point.layerType}`)) return null;
              return (
                <Circle
                  key={`flooded-zone-area-${point.id}`}
                  center={{ lat: parseFloat(point.latitude), lng: parseFloat(point.longitude) }}
                  radius={600}
                  options={{
                    fillColor: "#06b6d4",
                    fillOpacity: 0.18,
                    strokeColor: "#0891b2",
                    strokeOpacity: 0.9,
                    strokeWeight: 1.5,
                    clickable: false,
                    zIndex: 300,
                  }}
                />
              );
            })}

            {/* ─── Support Point Markers ─────────────────────────── */}
            {supportPointsData?.map((point) => {
              if (!point.latitude || !point.longitude || isNaN(point.latitude) || isNaN(point.longitude)) return null;
              if (!isItemVisible(`support-${point.layerType}`)) return null;
              const offset = supportPointOffsets[point.id];
              return (
                <CustomMarkerOverlay
                  key={`support-${point.id}`}
                  position={{ lat: parseFloat(point.latitude), lng: parseFloat(point.longitude) }}
                  offsetX={-21 + (offset?.x || 0)}
                  offsetY={-21 + (offset?.y || 0)}
                  zIndex={900}
                  onClick={() => {
                    openInfoWindow("support-point", point.id, {
                      lat: parseFloat(point.latitude),
                      lng: parseFloat(point.longitude),
                    });
                  }}
                >
                  <SupportPointMarkerContent layerType={point.layerType} />
                </CustomMarkerOverlay>
              );
            })}

            {activeInfoWindow?.type === "support-point" && (() => {
              const point = supportPointsData?.find((p) => p.id === activeInfoWindow.id);
              if (!point) return null;
              return (
                <InfoWindow position={activeInfoWindow.position} onCloseClick={() => setActiveInfoWindow(null)}>
                  <div dangerouslySetInnerHTML={{ __html: buildSupportPointPopupHtml(point) }} />
                </InfoWindow>
              );
            })()}

            {/* ──── NEW: Weather Markers on Map ──── */}
            <WeatherMapOverlay
              weatherData={weatherData}
              isItemVisible={isItemVisible}
            />

{/* ─── Weather Boundary Highlight ────────────────────── */}
{weatherBoundaryGeoJson?.geoJson &&
  geoJsonToGooglePaths(weatherBoundaryGeoJson.geoJson).map((path, i) => (
    <Polygon
      key={`weather-boundary-${weatherBoundaryGeoJson.gis_id}-${i}`}
      paths={path}
      options={{
        fillColor: "#2a9ed3ff",
        fillOpacity: 0.15,
        strokeColor: "#2a9ed3ff",
        strokeWeight: 3,
        strokeOpacity: 0.8,
        clickable: !drawingActive,   // must be true for hover to work, but disabled while drawing so clicks reach the map
        zIndex: 5,
      }}
      onMouseOver={(e) => {
        openInfoWindow("weather-boundary-tooltip", weatherBoundaryGeoJson.gis_id, {
          lat: e.latLng.lat(),
          lng: e.latLng.lng(),
        });
      }}
      onMouseOut={() => {
        if (activeInfoWindow?.type === "weather-boundary-tooltip")
          setActiveInfoWindow(null);
      }}
    />
  ))}

{/* Weather boundary tooltip */}
{activeInfoWindow?.type === "weather-boundary-tooltip" && weatherBoundaryGeoJson && (
  <InfoWindow
    position={activeInfoWindow.position}
    onCloseClick={() => setActiveInfoWindow(null)}
    options={{ disableAutoPan: true }}
  >
    <div style={{ fontFamily: "sans-serif", padding: "4px 6px" }}>
      <strong style={{ fontSize: 13 }}>{weatherBoundaryGeoJson.name}</strong>
      <div style={{ fontSize: 11, color: "#666", marginTop: 2, textTransform: "capitalize" }}>
        {weatherBoundaryGeoJson.type}
      </div>
    </div>
  </InfoWindow>
)}

              {/* ─── Alert: District boundaries ────────────────────── */}
            {isAlertsTab &&
              alertDistrictGeoJSON?.map((feature, idx) =>
                geoJsonToGooglePaths(feature).map((path, i) => (
                  <Polygon
                    key={`alert-district-${idx}-${i}`}
                    paths={path}
                    options={{
                      fillColor: "#ff7800",
                      fillOpacity: 0.08,
                      strokeColor: "#FF8C00",
                      strokeWeight: 2,
                      strokeOpacity: 1,
                      clickable: !drawingActive,
                      zIndex: 2,
                    }}
                    onMouseOver={(e) => {
                      openInfoWindow("alert-district-tooltip", idx, {
                        lat: e.latLng.lat(),
                        lng: e.latLng.lng(),
                      });
                    }}
                    onMouseOut={() => {
                      if (activeInfoWindow?.type === "alert-district-tooltip")
                        setActiveInfoWindow(null);
                    }}
                  />
                ))
              )}

            {/* Alert district tooltip */}
            {activeInfoWindow?.type === "alert-district-tooltip" && (
              <InfoWindow
                position={activeInfoWindow.position}
                onCloseClick={() => setActiveInfoWindow(null)}
                options={{ disableAutoPan: true }}
              >
                <div style={{ fontFamily: "sans-serif", fontSize: 12, padding: 4 }}>
                  <strong>
                    {alertDistrictGeoJSON?.[activeInfoWindow.id]?.properties?.name ||
                      `District ${activeInfoWindow.id}`}
                  </strong>
                </div>
              </InfoWindow>
            )}

            {/* ─── Alert: Block boundaries ───────────────────────── */}
            {isAlertsTab &&
              alertBlockGeoJSON?.map((feature, idx) =>
                geoJsonToGooglePaths(feature).map((path, i) => (
                  <Polygon
                    key={`alert-block-${idx}-${i}`}
                    paths={path}
                    options={{
                      fillColor: "transparent",
                      fillOpacity: 0,
                      strokeColor: "#2E5C85",
                      strokeWeight: 1.5,
                      strokeOpacity: 0.9,
                      clickable: !drawingActive,
                      zIndex: 3,
                    }}
                    onMouseOver={(e) => {
                      openInfoWindow("alert-block-tooltip", idx, {
                        lat: e.latLng.lat(),
                        lng: e.latLng.lng(),
                      });
                    }}
                    onMouseOut={() => {
                      if (activeInfoWindow?.type === "alert-block-tooltip")
                        setActiveInfoWindow(null);
                    }}
                  />
                ))
              )}

            {/* Alert block tooltip */}
            {activeInfoWindow?.type === "alert-block-tooltip" && (
              <InfoWindow
                position={activeInfoWindow.position}
                onCloseClick={() => setActiveInfoWindow(null)}
                options={{ disableAutoPan: true }}
              >
                <div style={{ fontFamily: "sans-serif", fontSize: 12, padding: 4 }}>
                  <strong>
                    {alertBlockGeoJSON?.[activeInfoWindow.id]?.properties?.name ||
                      `Block`}
                  </strong>
                  {alertBlockGeoJSON?.[activeInfoWindow.id]?.properties?.dist_name && (
                    <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>
                      {alertBlockGeoJSON[activeInfoWindow.id].properties.dist_name}
                    </div>
                  )}
                </div>
              </InfoWindow>
            )}

          </GoogleMap>

          {/* ─── Map Legend ─────────────────────────────────────── */}
          <MapLegend
            mapRef={mapInstance}
            isInfoWindowOpen={activeInfoWindow !== null}
            incidentMarkerData={incidentMarkerData}
            supportPointsData={supportPointsData}
            mapEvents={mapData1?.events}
            facilityCoordinates={facilityCoordinates}
            selectedFacilities={selectedFacilities}
            bmskdetails={bmskdetails}
            panchayatIncidentData={panchayatIncidentData}
            filteredFacilities={filteredFacilities}
            blockLayers={blockLayers}
            incidentBlockIds={incidentBlockIds}
            panchayatLayers={panchayatLayers}
            selectedPanchayatIds={selectedPanchayatIds}
            onFilterChange={handleFilterChange}
          />

         {/* ─── Custom Zoom Buttons — Top Left ─── */}
{regionManagement && (
  <div style={{ position: "absolute", top: 10, left: 10 }}>
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
        border: "2px solid #1e3a5f",
        borderRadius: 8,
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        height: "55px",
      }}
    >
      <button
        onClick={() => mapRef.current?.setZoom((mapRef.current.getZoom() || 7) + 1)}
        title="Zoom In"
        style={{
          width: 50, height: 50, backgroundColor: "white", border: "none",
          borderBottom: "1px solid rgba(0,0,0,0.15)", cursor: "pointer",
          fontSize: 22, fontWeight: "bold", color: "#333",
          display: "flex", alignItems: "center", justifyContent: "center",
          lineHeight: 1, transition: "background-color 0.15s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e0eff7")}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
      >+</button>
      <button
        onClick={() => mapRef.current?.setZoom((mapRef.current.getZoom() || 7) - 1)}
        title="Zoom Out"
        style={{
          width: 50, height: 50, backgroundColor: "white", border: "none",
          cursor: "pointer", fontSize: 26, fontWeight: "bold", color: "#333",
          display: "flex", alignItems: "center", justifyContent: "center",
          lineHeight: 1, transition: "background-color 0.15s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e0eff7")}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
      >−</button>
    </div>
  </div>
)}

{/* ─── Fullscreen Button — Top Right ─── */}
<button
  onClick={toggleFullscreen}
  title={isFullscreen ? "Exit Full Screen" : "Full Screen"}
  style={{
    position: "absolute", top: 10, right: 10, zIndex: 1000,
    width: 34, height: 34, borderRadius: 7,
    border: isFullscreen ? "2px solid #3b82f6" : "2px solid #1e3a5f",
    background: isFullscreen ? "linear-gradient(135deg,#eff6ff,#dbeafe)" : "#ffffff",
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: isFullscreen ? "0 2px 12px rgba(59,130,246,0.35)" : "0 2px 6px rgba(0,0,0,0.15)",
    transition: "all 0.2s ease",
  }}
  onMouseEnter={e => e.currentTarget.style.transform = "scale(1.08)"}
  onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
>
  {isFullscreen ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
    </svg>
  )}
</button>

{/* ─── Layer Switcher — Top Right (below fullscreen button) ─── */}
<div style={{ position: "absolute", top: 52, right: 10 }}>
  <button
    onClick={() => setIsLayerMenuOpen(!isLayerMenuOpen)}
    title="Map Layers"
    style={{
      width: 34, height: 34, backgroundColor: "white",
      border: "2px solid #1e3a5f", borderRadius: 7,
      cursor: "pointer", display: "flex", alignItems: "center",
      justifyContent: "center", boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
    }}
  >
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2 12L12 17L22 12" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2 17L12 22L22 17" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </button>
  {isLayerMenuOpen && (
    <div style={{
      position: "absolute", top: 44, right: 0, backgroundColor: "white",
      borderRadius: 10, boxShadow: "0 8px 24px rgba(16,24,40,0.14)",
      minWidth: 190, overflow: "hidden", border: "1px solid #EEF1F5",
      fontFamily: "'Poppins', 'Segoe UI', sans-serif",
    }}>
      <div style={{
        padding: "10px 14px", background: "#EBF4FA",
        borderBottom: "1px solid #EEF1F5",
        fontSize: "11px", fontWeight: 800, letterSpacing: ".3px",
        textTransform: "uppercase", color: "#2E6DA4",
      }}>
        Base Map
      </div>
      {[
        { key: "road", label: "Road View", Icon: MapIcon },
        { key: "satellite", label: "Satellite View", Icon: Satellite },
        { key: "terrain", label: "Terrain View", Icon: Mountain },
        { key: "hybrid", label: "Hybrid View", Icon: Layers },
      ].map(({ key, label, Icon }, idx) => {
        const isActive = activeBaseLayer === key;
        return (
          <div
            key={key}
            onClick={() => { setActiveBaseLayer(key); setIsLayerMenuOpen(false); mapRef.current?.setMapTypeId(MAP_TYPE_MAP[key]); }}
            style={{
              display: "flex", alignItems: "center", gap: "10px",
              padding: "10px 14px", cursor: "pointer",
              backgroundColor: isActive ? "#EBF4FA" : "white",
              boxShadow: isActive ? "inset 3px 0 0 #2E6DA4" : "none",
              borderBottom: idx < 3 ? "1px solid #F5F7FA" : "none",
              transition: "background-color 0.15s ease",
            }}
            onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = "#F9FAFB"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isActive ? "#EBF4FA" : "white"; }}
          >
            <Icon size={15} color={isActive ? "#2E6DA4" : "#667085"} />
            <span style={{
              flex: 1, fontSize: "12.5px",
              fontWeight: isActive ? 700 : 500,
              color: isActive ? "#1D2939" : "#344054",
            }}>
              {label}
            </span>
            {isActive && <Check size={14} color="#2E6DA4" />}
          </div>
        );
      })}
    </div>
  )}
</div>
        </div>
      </div>
    </FullScreen>
  );
};

// ─── Helper functions ─────────────────────────────────────────────────────

function findFacilityById(id, selectedFacilities, facilityCoordinates, filteredFacilities) {
  const fromSelected = selectedFacilities?.find((f) => f.id === id);
  if (fromSelected) return fromSelected;
  const fromCoords = facilityCoordinates?.find((f) => f.id === id || f.id === String(id));
  if (fromCoords) return { ...fromCoords, text: fromCoords.text || fromCoords.name };
  const fromFiltered = filteredFacilities?.find((f) => f.id === id);
  if (fromFiltered) return fromFiltered;
  return { text: `Facility ${id}`, value: "DEFAULT" };
}

// Pulls the earthquake-specific clause ("Earthquake of Magnitude: 7.3,
// Occurred on ... IST") and the "Expected Impact on Nearby Area" breakdown
// out of the raw description text so they can be shown as their own fields
// instead of buried in one long paragraph.
function parseIncidentDescription(description) {
  let remaining = description || "";

  // Two known phrasings from the backend:
  //  - "Earthquake of Magnitude: 7.3, Occurred on 2026-09-01, 04:52:09 IST" —
  //    followed by separate Lat/Long/Location text that should stay in the
  //    general description, so this one stops at "IST".
  //  - "Earthquake Alert: Magnitude 4.67 Richter detected at ... (Threshold:
  //    4 Richter)" — this variant has no trailing IST marker, so it's taken
  //    up to the "Expected Impact" clause (or end of string) instead.
  const eqMagnitudeMatch = remaining.match(/Earthquake of Magnitude:.*?IST/i);
  const eqAlertMatch = !eqMagnitudeMatch && remaining.match(/Earthquake Alert:.*?(?=\s*\.?\s*Expected Impact on Nearby Area:|$)/i);
  const eqMatch = eqMagnitudeMatch || eqAlertMatch;
  const earthquakeInfo = eqMatch ? eqMatch[0].trim() : null;
  if (eqMatch) remaining = remaining.replace(eqMatch[0], "").trim();

  // Each field is pulled out independently — not every incident's
  // description includes all of them (e.g. Zone is sometimes absent), and a
  // single all-or-nothing regex would fail to match the whole clause.
  const impactStart = remaining.search(/Expected Impact on Nearby Area:/i);
  let impactInfo = null;
  if (impactStart !== -1) {
    const impactClause = remaining.slice(impactStart);
    const field = (name) => impactClause.match(new RegExp(`${name}:\\s*([^,\\n]*)`, "i"))?.[1]?.trim();
    impactInfo = {
      buildings: field("Buildings"),
      hospitals: field("Hospitals"),
      schools: field("Schools"),
      population: field("Est\\.?\\s*Population"),
      zone: field("Zone"),
    };
    remaining = remaining.slice(0, impactStart).trim();
  }

  remaining = remaining.replace(/^[,.\s]+|[,.\s]+$/g, "").replace(/\n\s*\.\s*/g, " ").trim();

  return { earthquakeInfo, impactInfo, remaining };
}

// Builds the "Description" block: a short preview plus a native <details>
// toggle (no JS needed inside the InfoWindow's raw HTML) revealing the rest
// of the text and the Expected Impact fields as a small nested card.
function renderDescriptionBlock(description) {
  const { earthquakeInfo, impactInfo, remaining } = parseIncidentDescription(description);
  const PREVIEW_LENGTH = 90;
  const preview = remaining.length > PREVIEW_LENGTH ? `${remaining.slice(0, PREVIEW_LENGTH)}…` : remaining;

  let descriptionHtml = "";
  if (remaining) {
    descriptionHtml += `<p style="margin:0;font-size:12px;line-height:1.4;color:#495057;">${preview}</p>`;
  }
  // "View More" only ever reveals the Expected Impact breakdown — the
  // Lat/Long/Depth/Location text above is never repeated here.
  if (impactInfo) {
    const row = (label, value) => (value ? `<p style="margin:2px 0;"><strong>${label}:</strong> ${value}</p>` : "");
    descriptionHtml += `
      <details style="margin-top:6px;">
        <summary style="cursor:pointer;font-size:11.5px;font-weight:700;color:#2563eb;">View More</summary>
        <div style="margin-top:6px;padding:8px;background:#fff;border:1px solid #dee2e6;border-radius:6px;font-size:12px;color:#374151;">
          <p style="margin:0 0 6px;font-weight:700;color:#111827;">Expected Impact on Nearby Area</p>
          ${row("Buildings", impactInfo.buildings)}
          ${row("Hospitals", impactInfo.hospitals)}
          ${row("Schools", impactInfo.schools)}
          ${row("Est. Population", impactInfo.population)}
          ${row("Zone", impactInfo.zone)}
        </div>
      </details>
    `;
  }
  return { descriptionHtml, earthquakeInfo };
}

// Extra fields sourced from the "Select Incident" dropdown's own list data
// (severity, status, created by, block/district, lat-long, IDs) — richer
// than what the older incident-marker API returns, so shown alongside it.
function buildExtraIncidentDetailsHtml(details) {
  if (!details) return "";
  let lat = null;
  let lng = null;
  try {
    const center = JSON.parse(details.gis_config || "{}")?.incidentcenter;
    if (Array.isArray(center) && center.length >= 2) {
      lng = center[0];
      lat = center[1];
    }
  } catch (e) {
    // ignore malformed gis_config
  }

  const row = (label, value) => (value !== undefined && value !== null && value !== "" ? `<p style="margin: 4px 0; font-size: 12px;"><strong>${label}:</strong> ${value}</p>` : "");

  // District/Block are omitted here — the "Region Details" card below
  // already shows them, so no need to repeat.
  return `
    <details style="margin: 10px 0;">
      <summary style="cursor:pointer;font-size:11.5px;font-weight:700;color:#2563eb;">Incident Details</summary>
      <div style="background: #eef4fb; padding: 10px; border-radius: 6px; margin-top: 6px; border: 1px solid #d6e4f0;">
        ${row("Severity", details.severity)}
        ${row("Current Status", details.currentStatus ? String(details.currentStatus).replace(/_/g, " ") : "")}
        ${row("Created By", details.createdBy)}
        ${row("Lat, Long", lat !== null && lng !== null ? `${lat}, ${lng}` : "")}
        ${row("Incident ID", details.incidentSeqId)}
        ${row("Disaster Alert ID", details.disasterNotificationSeqId)}
      </div>
    </details>
  `;
}

function buildIncidentPopupContent(incidentMarkerData, matchingRegion, selectedIncidentDetails) {
  return `
    <div style="font-family: Arial, sans-serif; min-width: 280px; max-width: 400px;">
      ${incidentMarkerData.basicinfo
        ?.map(
          (info) => `
        <div style="background-color: #fff3cd; padding: 10px; border-radius: 6px; margin-bottom: 12px; border-left: 4px solid #ffa500;">
          <p style="margin: 0 0 6px; font-size: 14px; font-weight: bold; color: #cc0000;">${info.name}</p>
          <p style="margin: 4px 0; font-size: 12px;"><strong>Incident Category:</strong> <span style="color: #d9534f; font-weight: bold;">${info.cat}</span></p>
          <p style="margin: 4px 0; font-size: 12px;"><strong>Incident Date & Time:</strong> ${formatIncidentDateTime(info.time)}</p>
          ${buildExtraIncidentDetailsHtml(selectedIncidentDetails)}
          ${
            info.description
              ? (() => {
                  const { descriptionHtml, earthquakeInfo } = renderDescriptionBlock(info.description);
                  return `
                    ${earthquakeInfo ? `<p style="margin: 4px 0; font-size: 12px;"><strong>Earthquake Details:</strong> ${earthquakeInfo}</p>` : ""}
                    <div style="margin-top: 8px; padding: 8px; background: #f8f9fa; border-radius: 4px;">
                      <p style="margin: 0 0 4px; font-size: 12px;"><strong>Description:</strong></p>
                      ${descriptionHtml}
                    </div>
                  `;
                })()
              : ""
          }
        </div>
      `
        )
        .join("")}
      ${
        matchingRegion
          ? `
        <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; margin-top: 10px; border: 1px solid #dee2e6;">
          <h4 style="margin: 0 0 8px; font-size: 13px; color: #495057; border-bottom: 1px solid #dee2e6; padding-bottom: 4px;"><strong>Region Details</strong></h4>
          <p style="margin: 4px 0; font-size: 12px;"><strong>State:</strong> ${matchingRegion.dist || ""}</p>
          <p style="margin: 4px 0; font-size: 12px;"><strong>District:</strong> ${matchingRegion.blockname || ""}</p>
          <p style="margin: 4px 0; font-size: 12px;"><strong>Block:</strong> ${matchingRegion.name || ""}</p>
        </div>
      `
          : ""
      }
    </div>
  `;
}

function buildIncidentCenterPopup(incidentMarkerData, selectedIncidentDetails) {
  return `
    <div style="font-family: Arial, sans-serif; min-width: 280px; max-width: 400px;">
      ${incidentMarkerData.basicinfo
        ?.map(
          (info) => `
        <div style="background-color: #fff3cd; padding: 10px; border-radius: 6px; margin-bottom: 12px; border-left: 4px solid #ffa500;">
          <p style="margin: 0 0 6px; font-size: 14px; font-weight: bold; color: #cc0000;">${info.name}</p>
          <p style="margin: 4px 0; font-size: 12px;"><strong>Incident Category:</strong> <span style="color: #d9534f; font-weight: bold;">${info.cat}</span></p>
          <p style="margin: 4px 0; font-size: 12px;"><strong>Incident Date & Time:</strong> ${formatIncidentDateTime(info.time)}</p>
          ${buildExtraIncidentDetailsHtml(selectedIncidentDetails)}
          ${
            info.description
              ? (() => {
                  const { descriptionHtml, earthquakeInfo } = renderDescriptionBlock(info.description);
                  return `
                    ${earthquakeInfo ? `<p style="margin: 4px 0; font-size: 12px;"><strong>Earthquake Details:</strong> ${earthquakeInfo}</p>` : ""}
                    <div style="margin-top: 8px; padding: 8px; background: #f8f9fa; border-radius: 4px;">
                      <p style="margin: 0 0 4px; font-size: 12px;"><strong>Description:</strong></p>
                      ${descriptionHtml}
                    </div>
                  `;
                })()
              : ""
          }
        </div>
      `
        )
        .join("")}
      ${
        incidentMarkerData.regions?.length > 0
          ? `
        <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; margin-top: 10px; border: 1px solid #dee2e6;">
          <h4 style="margin: 0 0 8px; font-size: 13px; color: #495057; border-bottom: 1px solid #dee2e6; padding-bottom: 4px;"><strong>Region Details</strong></h4>
          ${incidentMarkerData.regions
            .map(
              (region) => `
            <div style="margin-bottom: 4px;">
              <p style="margin: 4px 0; font-size: 12px;"><strong>State:</strong> ${region.dist || ""}</p>
              <p style="margin: 4px 0; font-size: 12px;"><strong>District:</strong> ${region.blockname || ""}</p>
              <p style="margin: 4px 0; font-size: 12px;"><strong>Block:</strong> ${region.name || ""}</p>
            </div>
          `
            )
            .join("")}
        </div>
      `
          : ""
      }
    </div>
  `;
}

export default RMMap;